/**
 * The combat system. ARCHITECTURE.md §1.1, §4, §7.
 *
 * This is the only system that mutates gameplay state, and it drives physics explicitly so
 * that the within-tick order — timers, intent, move progression, integrate, build volumes,
 * resolve hits, separate, face — is guaranteed rather than inferred from a dependency graph.
 */

import {
  System, FIXED_STEP, THROW_BREAK_TICKS, WALL_SPLAT_SPEED, f60, signNonZero, clamp,
} from '../core/index.js';
import { S } from './states.js';
import { createFighterState, snapshotTransform, refreshDerived, resetCombo, yawForFacing }
  from './fighter-state.js';
import { CombatEmitter } from './events-out.js';
import { RoundDirector, createMatchState, PHASE } from './round.js';
import { HitResolver } from './hit-resolution.js';
import {
  tickTimers, resolveIntent, advanceMove, enterState, endMove, resolveWakeup,
  applyStanceMove, checkComboEnd, clampVelocity, beginKnockdown, forceState,
} from './state-machine.js';
import { buildCommandTable } from './notation.js';
import { getMoveTable, getCommandTable } from './moves/index.js';
import { computeHitDamage, applyComboProgress, addMeter, computeMeterGain } from './damage.js';

/** Ticks the grabber holds the victim before the throw executes, if no break happens. */
const THROW_HOLD_TICKS = THROW_BREAK_TICKS;

export class CombatSystem extends System {
  static id = 'combat-sim';
  static deps = ['core-input'];
  static budgetMs = 0.6;

  constructor() {
    super();
    /** @type {CombatEmitter|null} */
    this.emitter = null;
    this.rounds = new RoundDirector();
    this.resolver = new HitResolver();
    /** @type {Array<object>} per-fighter command tables, indexed by fighter slot. */
    this.commandTables = [null, null];
    /** @type {object|null} */
    this.physics = null;
    this._unsubs = [];
  }

  async init(ctx) {
    this.emitter = new CombatEmitter(ctx);
    this.physics = ctx.systems.get('physics');
    if (!this.physics) {
      throw new Error('combat-sim requires the "physics" system to be registered.');
    }

    ctx.data.moves = getMoveTable();
    ctx.sim.match = createMatchState(ctx);
    this.setFighters(ctx, ctx.app.selected[0], ctx.app.selected[1]);

    ctx.log.info(
      `combat: ${ctx.data.moves.all.length} moves loaded ` +
        `(${ctx.sim.fighters.map((f) => f.id).join(' vs ')})`,
    );
  }

  /**
   * Builds (or rebuilds) both fighter states and their command tables.
   * Called at init and whenever character select changes the roster.
   */
  setFighters(ctx, id0, id1) {
    ctx.sim.fighters.length = 0;
    ctx.sim.fighters.push(createFighterState(0, id0), createFighterState(1, id1));
    const table = ctx.data.moves;
    for (let i = 0; i < 2; i++) {
      const prefix = ctx.sim.fighters[i].prefix;
      this.commandTables[i] = getCommandTable(prefix) || buildCommandTable(table, prefix);
      // Resolve each fighter's move ids up front so hot paths never do a string lookup miss.
      const count = this.commandTables[i].starters.length;
      if (count === 0) {
        ctx.log.warn(`combat: fighter "${ctx.sim.fighters[i].id}" has no starter moves — check move data.`);
      }
    }
    this.rounds.startMatch(ctx, this.emitter);
    return ctx.sim.fighters;
  }

  /** Public API used by src/ui and tools. */
  startMatch(ctx) {
    this.rounds.startMatch(ctx, this.emitter);
  }

  rematch(ctx) {
    this.rounds.rematch(ctx, this.emitter);
  }

  pause(ctx) {
    this.rounds.pause(ctx);
  }

  resume(ctx) {
    this.rounds.resume(ctx);
  }

  setTrainingMode(ctx, on) {
    ctx.sim.match.training = on;
    ctx.flags.trainingMode = on;
    if (on) {
      ctx.sim.match.timerEnabled = false;
      ctx.sim.match.phase = PHASE.FIGHT;
    } else {
      ctx.sim.match.timerEnabled = true;
    }
  }

  // -------------------------------------------------------------------------
  // The tick
  // -------------------------------------------------------------------------

  fixedUpdate(step, ctx) {
    const emitter = this.emitter;
    const fighters = ctx.sim.fighters;
    if (fighters.length !== 2) return;

    // Interpolation snapshots must be taken before anything moves (ARCHITECTURE.md §2).
    snapshotTransform(fighters[0]);
    snapshotTransform(fighters[1]);

    // Bind this tick's input snapshots.
    fighters[0].inputs = ctx.input.players[0] || null;
    fighters[1].inputs = ctx.input.players[1] || null;

    const simulate = this.rounds.preStep(ctx, emitter);

    // Global hitstop counts down even while fighters are individually frozen, because the
    // camera and FX read it.
    if (ctx.sim.hitstop > 0) ctx.sim.hitstop--;

    // --- 1. timers ---
    const frozen = [false, false];
    for (let i = 0; i < 2; i++) {
      frozen[i] = tickTimers(fighters[i], step, ctx, emitter);
    }

    if (simulate) {
      // --- 2. throws in progress own both fighters; skip normal intent for them ---
      this._advanceThrows(ctx, emitter);

      // --- 3. intent ---
      for (let i = 0; i < 2; i++) {
        const f = fighters[i];
        if (frozen[i]) continue;
        if (f.throwPartner >= 0) continue;
        if (f.state === S.KNOCKDOWN_GROUND) {
          resolveWakeup(f, ctx, emitter, this.commandTables[i]);
          continue;
        }
        resolveIntent(f, fighters[1 - i], ctx, emitter, this.commandTables[i]);
      }

      // --- 4. move progression ---
      for (let i = 0; i < 2; i++) {
        const f = fighters[i];
        if (frozen[i]) continue;
        // Once a throw connects, _advanceThrows owns its hold/execute/release clocks. Letting the
        // generic move total advance in parallel can end the move while both roots are still
        // linked, leaving an idle attacker and permanently THROWN victim.
        if (f.throwPartner >= 0) {
          clampVelocity(f);
          continue;
        }
        if (f.move && (f.move.category === 'stance' || f.move.category === 'defence')
            && f.moveTick + 1 === f.move.tStartup + 1) {
          applyStanceMove(f, ctx, emitter);
          continue;
        }
        advanceMove(f, ctx, emitter);
        clampVelocity(f);
      }
    }

    // --- 5. integrate ---
    this.physics.integrate(step, ctx);

    // --- 6. post-integration reactions the physics pass discovered ---
    this._resolveWallAndLanding(ctx, emitter);

    // --- 7. build volumes and resolve hits ---
    if (simulate) {
      this.physics.buildDefensiveVolumes(ctx);
      this.physics.buildOffensiveVolumes(ctx);
      this.resolver.detect(ctx, this.physics);
      this.resolver.apply(ctx, emitter, this.physics);
    }

    // --- 8. separation and facing ---
    this.physics.separate(ctx);
    this.physics.correctFacing(ctx);

    // --- 9. bookkeeping ---
    for (let i = 0; i < 2; i++) {
      const f = fighters[i];
      refreshDerived(f);
      this._updateYaw(f, step);
      checkComboEnd(f, fighters[1 - i], ctx, emitter);
    }

    this.rounds.postStep(ctx, emitter);
  }

  /** Smoothly rotates the visual yaw toward the facing direction. Never snaps. */
  _updateYaw(f, step) {
    const target = yawForFacing(f.facing);
    let d = target - f.yaw;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    const maxStep = 14 * step; // radians/second — fast enough to feel instant, slow enough to read
    f.yaw += clamp(d, -maxStep, maxStep);
  }

  // -------------------------------------------------------------------------
  // Throw sequencing
  // -------------------------------------------------------------------------

  _advanceThrows(ctx, emitter) {
    const fighters = ctx.sim.fighters;
    for (let i = 0; i < 2; i++) {
      const attacker = fighters[i];
      // BOTH fighters in a grapple carry `throwPartner`, so the partner link alone does not
      // identify the grabber. Only the grabber owns a `throwMoveId` — checking it is what keeps
      // the victim from being advanced as though they were performing the throw.
      if (attacker.throwPartner < 0 || !attacker.throwMoveId) continue;
      const victim = fighters[attacker.throwPartner];

      // Sample the break input BEFORE the hitstop check. A grab applies 8 ticks of hitstop, and
      // a press edge only exists on the tick it happens — skipping input during the freeze would
      // silently eat every break attempt made in the first 8 ticks of the window.
      if (attacker.state === S.THROW_HOLD && victim.throwBreakTicks > 0 && !victim.throwBreakArmed) {
        const snap = victim.inputs;
        if (snap && (snap.pressedLp || snap.pressedLk || snap.pressedHp || snap.pressedHk)) {
          victim.throwBreakArmed = true;
        }
      }

      if (attacker.frozenTicks > 0) continue;
      const move = ctx.data.moves.byId.get(attacker.throwMoveId);
      if (!move) {
        ctx.log.warn(`combat: throw move "${attacker.throwMoveId}" missing; releasing grapple`);
        this._releaseThrow(ctx, emitter, attacker, victim);
        continue;
      }

      attacker.throwTick++;

      // Keep the pair welded together so the throw animation has a stable contact point.
      const offset = (attacker.stats.pushboxRadius + victim.stats.pushboxRadius) * 0.82;
      victim.x = attacker.x + attacker.facing * offset;
      victim.z = attacker.z;
      victim.vx = 0;
      victim.vy = 0;
      victim.vz = 0;
      victim.facing = -attacker.facing;

      if (attacker.state === S.THROW_HOLD) {
        // --- break window: resolves as soon as the freeze ends if a break was armed ---
        if (victim.throwBreakArmed) {
          this._breakThrow(ctx, emitter, attacker, victim, move);
          continue;
        }
        if (attacker.throwTick >= THROW_HOLD_TICKS) {
          this._executeThrow(ctx, emitter, attacker, victim, move);
        }
        continue;
      }

      if (attacker.state === S.THROW_EXECUTE) {
        // --- throw chains: Bulwark can extend into a follow-up throw on a timed input ---
        if (move.cancels.length > 0 && attacker.throwTick <= move.tCancelClose
            && attacker.throwTick >= move.tCancelOpen) {
          const snap = attacker.inputs;
          if (snap && (snap.pressedHp || snap.pressedHk)) {
            const nextId = move.cancels[Math.min(attacker.throwChainIndex, move.cancels.length - 1)];
            const next = ctx.data.moves.byId.get(nextId);
            if (next && next.isThrow) {
              attacker.throwChainIndex++;
              attacker.move = next;
              attacker.moveId = next.id;
              attacker.moveTick = next.tStartup + next.tActive;
              attacker.moveHasHit = true;
              attacker.hitboxUsed.fill(0);
              attacker.throwMoveId = next.id;
              attacker.throwTick = 0;
              victim.throwBreakTicks = f60(5); // chains are harder to break than the initial grab
              enterState(attacker, S.THROW_HOLD, ctx, emitter, next.id);
              const tp = emitter.begin('combat:throw');
              tp.attacker = attacker.index;
              tp.defender = victim.index;
              tp.moveId = next.id;
              tp.moveName = next.name;
              tp.damage = next.damage;
              tp.breakWindow = victim.throwBreakTicks;
              tp.chainIndex = attacker.throwChainIndex;
              emitter.send('combat:throw');
              continue;
            }
          }
        }
        if (attacker.throwTick >= move.tRecovery) {
          this._releaseThrow(ctx, emitter, attacker, victim);
        }
      }
    }
  }

  _breakThrow(ctx, emitter, attacker, victim, move) {
    const p = emitter.begin('combat:throw-break');
    p.attacker = attacker.index;
    p.defender = victim.index;
    p.moveId = move.id;
    p.breakTick = attacker.throwTick;
    emitter.send('combat:throw-break');

    attacker.throwPartner = -1;
    attacker.throwMoveId = '';
    attacker.throwTick = 0;
    attacker.move = null;
    attacker.moveId = '';
    attacker.actionLockTicks = f60(11);
    enterState(attacker, S.THROW_BROKEN, ctx, emitter);

    victim.throwPartner = -1;
    victim.throwBreakTicks = 0;
    victim.actionLockTicks = f60(7);
    enterState(victim, S.THROW_TECH, ctx, emitter);

    // Push the pair apart so the break reads as a mutual shove.
    const away = signNonZero(victim.x - attacker.x);
    victim.vx = away * 2.4;
    attacker.vx = -away * 1.8;

    emitter.cue('throw-break', victim.x, victim.y + 1.1, victim.z, 1, 1, 0, 'body');
    emitter.impact('throw-burst', victim.x, victim.y + 1.1, victim.z, away, 0.2, 0, 0.7, 'body', 0.8);
    emitter.cameraImpulse('throw-break', 0.4, away, 0.1, 0, f60(6), 16, 0);
    emitter.cinematic('throw', attacker.index, victim.index, 0, 'end');
  }

  _executeThrow(ctx, emitter, attacker, victim, move) {
    enterState(attacker, S.THROW_EXECUTE, ctx, emitter, move.id);
    attacker.throwTick = 0;
    victim.throwBreakTicks = 0;

    const hc = { counter: false, punish: false, airborne: false, juggleCount: 0 };
    const stanceMul = attacker.stance === 'anchor' ? attacker.stats.anchorThrowDamageMul : 1;
    const { damage, rawDamage, scaleUsed, nextScale } = computeHitDamage(attacker, move, hc);
    const finalDamage = Math.max(1, Math.round(damage * stanceMul));

    victim.health = Math.max(0, victim.health - finalDamage);
    victim.damageTakenRound += finalDamage;
    victim.perfectRound = false;
    attacker.damageDealtRound += finalDamage;
    applyComboProgress(attacker, move, finalDamage, nextScale, ctx.time.tick);
    addMeter(victim, computeMeterGain(victim, 'damage-taken', finalDamage));

    const fatal = victim.health <= 0;
    emitter.damage(victim.index, finalDamage, victim.health, victim.maxHealth, move.id, fatal);
    if (fatal) {
      victim.defeated = true;
      emitter.defeat(victim.index, 'throw');
    }

    // Presentation: a throw slam is the heaviest non-finisher impact in the game.
    emitter.cue('throw-slam', victim.x, victim.y + 0.4, victim.z, 1, 1, 0, 'wet-concrete');
    emitter.impact('ground-crack', victim.x, victim.y + 0.1, victim.z, 0, 1, 0, 1.35, 'wet-concrete', 1.4);
    emitter.impact('water-splash', victim.x, victim.y + 0.05, victim.z, 0, 1, 0, 1.1, 'puddle', 1.2);
    emitter.cue('exert-heavy', attacker.x, attacker.y + 1.5, attacker.z, 0.9,
      attacker.id === 'kestrel' ? 1.1 : 0.86, 1);
    emitter.cameraImpulse('throw', 0.9, 0, -1, 0, f60(12), 18, 0.05);
    void rawDamage;
    void scaleUsed;
  }

  _releaseThrow(ctx, emitter, attacker, victim) {
    attacker.throwPartner = -1;
    attacker.throwMoveId = '';
    attacker.throwTick = 0;
    attacker.throwChainIndex = 0;
    endMove(attacker, ctx, emitter, S.IDLE);

    victim.throwPartner = -1;
    victim.throwBreakTicks = 0;
    // The victim is already welded in front of the attacker. Continue in that direction so
    // release cannot send the root through the attacker's pushbox before the hard landing.
    const away = attacker.facing;
    victim.vx = away * 4.2;
    victim.vy = 3.0;
    victim.headTowardOpponent = true;
    beginKnockdown(victim, ctx, emitter, true, true);
    emitter.cinematic('throw', attacker.index, victim.index, 0, 'end');
  }

  // -------------------------------------------------------------------------
  // Wall and landing reactions
  // -------------------------------------------------------------------------

  _resolveWallAndLanding(ctx, emitter) {
    const fighters = ctx.sim.fighters;
    for (let i = 0; i < 2; i++) {
      const f = fighters[i];
      const wall = this.physics.wallEvents[i];
      const land = this.physics.landEvents[i];

      // --- wall contact while in hitstun becomes a splat or a bounce ---
      if (wall.hit && (f.inHitstun || f.state === S.THROWN)) {
        const splat = this.physics.isWallSplatSpeed(f, wall.speed) && !f.wallSplatUsed;
        if (splat) {
          f.wallSplatUsed = true;
          f.wallStunTicks = f60(22);
          // Wall stun replaces hitstun — see the same note in hit-resolution.js.
          f.hitstunTicks = 0;
          f.vx = 0;
          f.vy = Math.max(f.vy, 0);
          const attacker = fighters[1 - i];
          attacker.comboWallUsed = true;
          enterState(f, S.WALL_SPLAT, ctx, emitter);
          const p = emitter.begin('combat:wall-splat');
          p.fighter = i;
          p.wallSide = wall.side;
          p.impactSpeed = wall.speed;
          p.x = f.x;
          p.y = f.y + 1.0;
          p.z = f.z;
          p.stunTicks = f.wallStunTicks;
          emitter.send('combat:wall-splat');
          emitter.cue('wall-impact', f.x, f.y + 1.05, f.z, 1, 1, 0, 'concrete');
          emitter.impact('wall-crack', f.x, f.y + 1.05, f.z, -wall.side, 0, 0, 1.25, 'concrete', 1.3);
          emitter.cameraImpulse('wall-splat', 0.9, -wall.side, 0.1, 0, f60(11), 22, 0.04);
        } else if (wall.speed > 1.2) {
          const p = emitter.begin('combat:wall-bounce');
          p.fighter = i;
          p.wallSide = wall.side;
          p.impactSpeed = wall.speed;
          p.x = f.x;
          p.y = f.y + 1.0;
          p.z = f.z;
          emitter.send('combat:wall-bounce');
          f.vx = -wall.side * Math.min(2.2, wall.speed * 0.35);
          emitter.cue('wall-impact', f.x, f.y + 1.0, f.z, 0.6, 1.1, 1, 'concrete');
          emitter.impact('dust-puff', f.x, f.y + 1.0, f.z, -wall.side, 0.1, 0, 0.6, 'concrete', 0.8);
        }
      }

      // --- landing ---
      if (land.landed) {
        if (f.state === S.KNOCKDOWN_FALL) {
          // handled by tickTimers on the next tick
        } else if (f.airborne || f.state === S.AIRBORNE || f.state === S.AIR_ATTACK) {
          if (f.inHitstun) {
            f.headTowardOpponent = false;
            beginKnockdown(f, ctx, emitter, land.hard, true);
          } else {
            enterState(f, land.hard ? S.HARD_LANDING : S.LANDING, ctx, emitter);
            f.actionLockTicks = land.hard ? f60(14) : f60(4);
            f.vx *= 0.3;
          }
          emitter.cue(land.hard ? 'land-hard' : 'land-soft', f.x, f.y + 0.1, f.z,
            land.hard ? 1 : 0.6, 1, 0, 'wet-concrete');
          emitter.impact('water-splash', f.x, f.y + 0.03, f.z, 0, 1, 0,
            land.hard ? 1 : 0.55, 'puddle', land.hard ? 1.1 : 0.7);
          if (land.hard) {
            emitter.cameraImpulse('land', 0.35, 0, -1, 0, f60(6), 15, 0);
          }
        }
        f.juggleCount = 0;
        f.juggleGravityMul = 1;
      }
    }
  }

  // -------------------------------------------------------------------------
  // Queries for AI, UI, training mode and tools
  // -------------------------------------------------------------------------

  /** Frame advantage the given fighter currently holds, in f60 frames. Negative = at a deficit. */
  frameAdvantage(ctx, index) {
    const me = ctx.sim.fighters[index];
    const them = ctx.sim.fighters[1 - index];
    return Math.round((this._freeIn(them) - this._freeIn(me)) / 2);
  }

  /** Ticks until this fighter can act again. */
  _freeIn(f) {
    if (f.frozenTicks > 0) return f.frozenTicks + this._freeIn({ ...f, frozenTicks: 0 });
    let t = 0;
    if (f.hitstunTicks > 0) t = Math.max(t, f.hitstunTicks);
    if (f.blockstunTicks > 0) t = Math.max(t, f.blockstunTicks);
    if (f.actionLockTicks > 0) t = Math.max(t, f.actionLockTicks);
    if (f.move) t = Math.max(t, f.move.tTotal - f.moveTick);
    if (f.state === S.KNOCKDOWN_GROUND) {
      t = Math.max(t, (f.knockdownHard ? f60(34) : f60(20)) - f.groundTicks);
    }
    return t;
  }

  /** Resets both fighters to a training-mode position preset. */
  resetPositions(ctx, preset = 'neutral') {
    const [a, b] = ctx.sim.fighters;
    const layouts = {
      neutral: [-1.05, 1.05],
      close: [-0.42, 0.42],
      far: [-3.2, 3.2],
      'wall-left': [-4.9, -3.6],
      'wall-right': [4.9, 3.6],
      'corner-p1': [-5.0, -3.9],
      'corner-p2': [5.0, 3.9],
    };
    const layout = layouts[preset] || layouts.neutral;
    for (let i = 0; i < 2; i++) {
      const f = ctx.sim.fighters[i];
      f.x = layout[i];
      f.y = 0;
      f.z = 0;
      f.vx = 0;
      f.vy = 0;
      f.vz = 0;
      f.prevX = f.x;
      f.prevY = f.y;
      f.prevZ = f.z;
      f.hitstunTicks = 0;
      f.blockstunTicks = 0;
      f.frozenTicks = 0;
      f.actionLockTicks = 0;
      f.invulnTicks = 0;
      f.throwPartner = -1;
      f.throwBreakTicks = 0;
      f.wallSplatUsed = false;
      f.juggleCount = 0;
      f.groundTicks = 0;
      f.move = null;
      f.moveId = '';
      f.moveTick = 0;
      f.moveHasHit = false;
      f.hitboxUsed.fill(0);
      resetCombo(f);
      f.facing = i === 0 ? signNonZero(layout[1] - layout[0]) : signNonZero(layout[0] - layout[1]);
      f.yaw = yawForFacing(f.facing);
      f.prevYaw = f.yaw;
      // A training reset reconstructs the fighter rather than transitioning them, so it
      // deliberately bypasses the transition table (see forceState).
      forceState(f, S.IDLE, ctx, this.emitter);
    }
    ctx.sim.hitstop = 0;
    return layout;
  }

  /** Restores both fighters to full health. Training mode's infinite-health option. */
  refillHealth(ctx) {
    for (const f of ctx.sim.fighters) {
      f.health = f.maxHealth;
      f.guardMeter = f.guardMax;
    }
  }

  dispose() {
    for (const off of this._unsubs) off();
    this._unsubs.length = 0;
    this.emitter = null;
    this.physics = null;
    this.commandTables[0] = null;
    this.commandTables[1] = null;
  }
}

export { PHASE, WALL_SPLAT_SPEED, FIXED_STEP };
